/* eslint-disable @typescript-eslint/naming-convention */
import * as yup from 'yup';

const fields = [
  'arthritis',
  'cancer',
  'dementia_alzheimers',
  'diabetes',
  'heart_disease',
  'dry_mouth',
  'obesity',
  'pregnancy_or_breastfeeding',
  'respiratory_conditions',
  'stroke',
  'medical_condition_none_of_the_above',
];

export const medicalConditionsValidationSchema = yup
  .object({
    user_id: yup.number().optional(),
    arthritis: yup.boolean().optional(),
    cancer: yup.boolean().optional(),
    dementia_alzheimers: yup.boolean().optional(),
    diabetes: yup.boolean().optional(),
    heart_disease: yup.boolean().optional(),
    dry_mouth: yup.boolean().optional(),
    obesity: yup.boolean().optional(),
    pregnancy_or_breastfeeding: yup.boolean().optional(),
    respiratory_conditions: yup.boolean().optional(),
    stroke: yup.boolean().optional(),
    conditions_not_listed: yup
      .string()
      .max(250, 'Maximum 250 characters')
      .nullable()
      .optional()
      .test('not-with-none-of-the-above', 'Cannot enter text when "None of the above" is selected', function (value) {
        const noneSelected = this.parent.medical_condition_none_of_the_above === true;
        return noneSelected ? !value || value.trim() === '' : true;
      })
      .test('required-if-selected', 'Please describe the condition(s)', function (value) {
        // This handles the case where conditions_not_listed is "selected" (i.e. not empty but invalid)
        const raw = value as string | undefined;
        if (raw === 'true' || raw?.trim() === 'true') {
          return false;
        }
        return true;
      }),
    medical_condition_none_of_the_above: yup
      .boolean()
      .optional()
      .test('exclusive-none', 'Cannot select other conditions when "None of the above" is selected', function (value) {
        if (value !== true) return true;

        const extendedFields = [...fields, 'conditions_not_listed'];
        const otherSelected = extendedFields.some((key) => {
          const isSelected = this.parent[key] === true;
          if (key === 'conditions_not_listed') {
            return isSelected && typeof this.parent[key] === 'string' && this.parent[key].trim() !== '';
          }
          if (key === 'medical_condition_none_of_the_above') {
            return false;
          }
          return isSelected;
        });
        return !otherSelected;
      }),
  })
  .test('at-least-one-selected', 'At least one condition must be selected', function () {
    const formData = (this.options as any).originalValue || {};

    const anySelected = fields.some((field) => formData[field] === true);
    if (
      formData.conditions_not_listed &&
      typeof formData.conditions_not_listed === 'string' &&
      formData.conditions_not_listed.trim() !== ''
    ) {
      return true;
    }
    return anySelected;
  });

export const medicalHistoryValidationSchema = yup.object({
  user_id: yup.number().optional(),

  under_care_of_physician: yup.boolean().nullable().optional(),
  conditions_treated: yup
    .string()
    .nullable()
    .when('under_care_of_physician', {
      is: true,
      then: (schema) => schema.required('Please describe what conditions are being treated.').min(1),
      otherwise: (schema) =>
        schema
          .nullable()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .notRequired(),
    }),

  taking_medication: yup.boolean().nullable().optional(),
  medications: yup
    .string()
    .nullable()
    .when('taking_medication', {
      is: true,
      then: (schema) => schema.required('Please list your medications.').min(1),
      otherwise: (schema) =>
        schema
          .nullable()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .notRequired(),
    }),

  allergic_to_medications: yup.boolean().nullable().optional(),
  medications_allergies: yup
    .string()
    .nullable()
    .when('allergic_to_medications', {
      is: true,
      then: (schema) => schema.required('Please specify your allergies.').min(1),
      otherwise: (schema) =>
        schema
          .nullable()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .notRequired(),
    }),

  using_substances: yup.array().of(yup.string()).optional(),

  interested_in_stopping: yup
    .mixed<true | false | null>()
    .nullable()
    .test('interested-in-stopping-logic', 'Please indicate if you’re interested in quitting.', function (value) {
      const { tobacco_products, recreational_drugs, prescription_medication_for_non_medical_reasons } = this.parent;

      const anySubstanceUsed =
        tobacco_products || recreational_drugs || prescription_medication_for_non_medical_reasons;

      if (anySubstanceUsed) {
        return value === true || value === false; // null NOT allowed
      }

      return value === null; // must be null when no substance used
    }),

  height_feet: yup
    .mixed()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .required('Height (feet) is required')
    .test('valid-feet', 'Height (feet) must be a positive number', (value) => {
      return value > 0;
    }),

  height_inches: yup
    .mixed()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .required('Height (inches) is required')
    .test('valid-inches', 'Height (inches) must be between 0 and 11', (value) => value >= 0 && value <= 11),
  weight: yup
    .mixed()
    .transform((value, originalValue) => {
      // Convert empty string to undefined
      return originalValue === '' ? undefined : value;
    })
    .required('Weight is required')
    .test('positive-number', 'Weight must be a positive number', (value) => {
      return value > 0;
    }),
});

export const combinedValidationSchema = medicalHistoryValidationSchema.concat(medicalConditionsValidationSchema);

const answeredToggle = (message: string): yup.BooleanSchema<boolean | null | undefined> =>
  yup
    .boolean()
    .nullable()
    .test('answered', message, (value) => value === true || value === false);

// An unanswered toggle is submitted as "No", so on the profile the patient has to
// answer these before saving instead of silently recording an answer they never gave.
export const profileCombinedValidationSchema = combinedValidationSchema.shape({
  under_care_of_physician: answeredToggle('Please indicate if you are under the care of a physician.'),
  taking_medication: answeredToggle('Please indicate if you are taking any medications.'),
  allergic_to_medications: answeredToggle('Please indicate if you are allergic to any medications.'),
});
